import * as Sentry from '@sentry/react';
import React, { ErrorInfo, ReactNode } from 'react';

import { Box } from '../Box';
import { Button } from '../Button';
import { H3 } from '../Heading';
import { IconWarning } from '../Icon';
import Loader from '../Loader';
import { Text } from '../Text';

const RELOAD_TIMESTAMP_KEY = 'chunk-reload-attempted-at';
const RELOAD_COOLDOWN_MS = 10000;

const CHUNK_ERROR_PATTERN =
  /loading (css )?chunk .* failed|failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed/i;

const isChunkLoadError = (error: Error) =>
  error.name === 'ChunkLoadError' || CHUNK_ERROR_PATTERN.test(error.message || '');

// A failed chunk usually means the tab is holding a stale bundle reference (a
// deploy replaced the hashed filenames), so a full reload fixes it. The
// timestamp guards against a reload loop if the chunk is genuinely missing.
const reloadOnce = () => {
  try {
    const lastAttempt = Number(window.sessionStorage.getItem(RELOAD_TIMESTAMP_KEY)) || 0;

    if (Date.now() - lastAttempt < RELOAD_COOLDOWN_MS) {
      return false;
    }

    window.sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(Date.now()));
  } catch {
    return false;
  }

  window.location.reload();

  return true;
};

interface IProps {
  children: ReactNode;
  resetKey?: string;
}

interface IState {
  hasError: boolean;
  reloading: boolean;
}

class ChunkErrorBoundary extends React.Component<IProps, IState> {
  state: IState = { hasError: false, reloading: false };

  static getDerivedStateFromError(error: Error): IState {
    return { hasError: true, reloading: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });

    if (isChunkLoadError(error) && !reloadOnce()) {
      this.setState({ reloading: false });
    }
  }

  componentDidUpdate(prevProps: IProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, reloading: false });
    }
  }

  render() {
    const { hasError, reloading } = this.state;

    if (reloading) {
      return <Loader mt="40vh" />;
    }

    if (hasError) {
      return (
        <Box
          display="flex"
          mt={[6, 9, 9, 12]}
          p={[4, 4, 6]}
          maxWidth={600}
          mx="auto"
          flexDirection="column"
          alignItems="center"
        >
          <IconWarning fg="fg.alert" size="38px" />
          <H3 mt={4.5} textAlign="center">
            Något gick fel...
          </H3>
          <Text textAlign="center">
            Sidan kunde inte laddas. Prova att ladda om sidan igen om en stund.
          </Text>
          <Button mt={4} variant="outline" onClick={() => window.location.reload()}>
            Ladda om
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
